/* @vendored-from tactic:src/modules/greenhouse/lib/geometry/core.ts kernel-v1
 * NO EDITAR EN PEST: este archivo se regenera con scripts/sync-greenhouse-kernel.mjs
 * desde el repo de Tactic (fuente de verdad del kernel geométrico). */
import type { Point2D, Segment } from "../../types/editor";

/** Remove duplicate consecutive points */
export function normalizeRing(points: Point2D[]): Point2D[] {
  if (points.length < 2) return [...points];
  const result: Point2D[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (Math.abs(curr.x - prev.x) > 0.001 || Math.abs(curr.y - prev.y) > 0.001) {
      result.push(curr);
    }
  }
  return result;
}

/** Check if ring is closed (first point ≈ last point) */
export function isRingClosed(points: Point2D[]): boolean {
  if (points.length < 3) return false;
  const first = points[0];
  const last = points[points.length - 1];
  return Math.abs(first.x - last.x) < 0.01 && Math.abs(first.y - last.y) < 0.01;
}

/** Ensure ring is closed by appending first point if needed */
export function closeRing(points: Point2D[]): Point2D[] {
  if (points.length < 3) return [...points];
  if (isRingClosed(points)) return [...points];
  return [...points, { ...points[0] }];
}

/** Extract segments from a ring of points */
export function getSegments(points: Point2D[], ringId: string): Segment[] {
  const closed = closeRing(normalizeRing(points));
  const segments: Segment[] = [];
  for (let i = 0; i < closed.length - 1; i++) {
    segments.push({
      segmentId: `${ringId}-seg-${i}`,
      start: closed[i],
      end: closed[i + 1],
      ringId,
      index: i,
    });
  }
  return segments;
}

/** Calculate length of a segment in meters */
export function calculateSegmentLength(start: Point2D, end: Point2D): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Calculate bounding box of a set of points */
export function calculateBounds(points: Point2D[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** Get midpoint of a segment */
export function segmentMidpoint(start: Point2D, end: Point2D): Point2D {
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
}

/** Get angle of a segment in radians */
export function segmentAngle(start: Point2D, end: Point2D): number {
  return Math.atan2(end.y - start.y, end.x - start.x);
}

/** Distance from a point to a line segment */
export function distanceToSegment(p: Point2D, a: Point2D, b: Point2D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return calculateSegmentLength(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const proj = { x: a.x + t * dx, y: a.y + t * dy };
  return calculateSegmentLength(p, proj);
}

/** Snap a point to grid */
export function snapToGrid(point: Point2D, gridSize: number): Point2D {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
}

/** Reshape outer ring points so segments match the given dimensions.
 *  For 4-point rectangles: rebuild a clean rect from origin.
 *  For others: scale each segment along its direction and chain-propagate. */
export function reshapeGeometryFromDimensions(
  points: Point2D[],
  ringId: string,
  dimensions: { segmentId: string; lengthMeters: number; locked?: boolean }[]
): Point2D[] {
  const norm = normalizeRing(points);
  if (norm.length < 3) return [...norm];

  const dimMap = new Map(dimensions.map((d) => [d.segmentId, d.lengthMeters]));
  const segments = getSegments(norm, ringId);

  // Rectangular fast-path: exactly 4 segments
  if (segments.length === 4) {
    // seg0=top, seg1=right, seg2=bottom, seg3=left (typical)
    const w0 = dimMap.get(segments[0].segmentId) ?? calculateSegmentLength(segments[0].start, segments[0].end);
    const w2 = dimMap.get(segments[2].segmentId) ?? calculateSegmentLength(segments[2].start, segments[2].end);
    const h1 = dimMap.get(segments[1].segmentId) ?? calculateSegmentLength(segments[1].start, segments[1].end);
    const h3 = dimMap.get(segments[3].segmentId) ?? calculateSegmentLength(segments[3].start, segments[3].end);

    const width = (w0 + w2) / 2;
    const height = (h1 + h3) / 2;

    // Keep origin at first point, use first segment direction for orientation
    const origin = norm[0];
    const angle = Math.atan2(segments[0].end.y - segments[0].start.y, segments[0].end.x - segments[0].start.x);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    // Perpendicular (90° clockwise for typical CW winding)
    const pcos = Math.cos(angle + Math.PI / 2);
    const psin = Math.sin(angle + Math.PI / 2);

    return [
      { x: origin.x, y: origin.y },
      { x: origin.x + cos * width, y: origin.y + sin * width },
      { x: origin.x + cos * width + pcos * height, y: origin.y + sin * width + psin * height },
      { x: origin.x + pcos * height, y: origin.y + psin * height },
    ];
  }

  // General fallback: scale each segment and chain-propagate
  const newPoints: Point2D[] = [{ ...norm[0] }];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const currentLen = calculateSegmentLength(seg.start, seg.end);
    const desiredLen = dimMap.get(seg.segmentId) ?? currentLen;
    if (currentLen < 0.001) {
      newPoints.push({ ...newPoints[newPoints.length - 1] });
      continue;
    }
    const scale = desiredLen / currentLen;
    const prev = newPoints[newPoints.length - 1];
    const dx = seg.end.x - seg.start.x;
    const dy = seg.end.y - seg.start.y;
    newPoints.push({
      x: prev.x + dx * scale,
      y: prev.y + dy * scale,
    });
  }
  // Remove closing point (last == first approximation) — return open ring
  const uniqueCount = norm.length;
  return newPoints.slice(0, uniqueCount);
}
